import { useEffect, useState } from "react";
import { api, act, useFetch } from "@/lib/api";
import { PageHeader, Panel, Stat } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const aud = (v) => (v > 0 && v < 0.01 ? "<A$0.01" : `A$${Number(v || 0).toFixed(2)}`);
const shift = (ym, back) => { const [y, m] = ym.split("-").map(Number); const t = y * 12 + (m - 1) - back; return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`; };
const PRICE_FIELDS = [
  ["azure_per_1000_usd", "Azure email, per 1,000 (US$)"], ["image_usd", "AI image, each (US$)"], ["video_usd", "AI video, each (US$)"],
  ["voice_usd", "Voiceover, each (US$)"], ["aud_per_usd", "A$ per US$1"], ["fixed_monthly_aud", "Fixed monthly costs (A$)"], ["budget_aud", "Monthly budget - email me when passed (A$)"],
];

const Prices = ({ prices, onSaved }) => {
  const [p, setP] = useState(prices);
  useEffect(() => setP(prices), [prices]);
  const save = async () => { if (await act(() => api.patch("/costs/prices", p), "Prices saved")) onSaved(); };
  return (
    <Panel title="Prices used for the estimate" data-testid="cost-prices">
      <div className="grid sm:grid-cols-2 gap-3">
        {PRICE_FIELDS.map(([k, l]) => (
          <label key={k} className="text-xs text-zinc-400 space-y-1">
            <span>{l}</span>
            <Input type="number" step="0.01" min="0" value={p[k] ?? ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="bg-black h-9" data-testid={`cost-price-${k}`} />
          </label>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Fixed monthly costs: add things like Microsoft 365 licences, domains and hosting so the total shows your full monthly spend.</p>
      <Button className="mt-3" onClick={save} data-testid="cost-prices-save">Save prices</Button>
    </Panel>
  );
};

const ByCampaign = ({ data }) => (
  <Panel title="By campaign" data-testid="cost-by-campaign">
    <table className="w-full text-sm">
      <tbody>
        {data.by_campaign.map((c) => (
          <tr key={c.campaign_id} className="border-b border-border/60" data-testid={`cost-campaign-${c.campaign_id}`}>
            <td className="py-2 text-white">{c.name}</td>
            <td className="py-2 text-right text-xs text-zinc-500">{c.emails.toLocaleString()} emails ({c.azure_emails.toLocaleString()} via Azure) · {c.ai_calls} AI calls</td>
            <td className="py-2 text-right font-mono text-white w-28">{aud(c.aud)}</td>
          </tr>
        ))}
        <tr data-testid="cost-campaign-shared">
          <td className="py-2">Shared<div className="text-[11px] text-zinc-500">AI and media not tied to one campaign, newsletters and waitlist emails</div></td>
          <td />
          <td className="py-2 text-right font-mono text-white">{aud(data.shared_aud)}</td>
        </tr>
      </tbody>
    </table>
    {!data.by_campaign.length && <p className="text-xs text-zinc-500 mt-2" data-testid="cost-campaign-empty">No campaign spend this month yet.</p>}
  </Panel>
);

export default function Costs() {
  const [offset, setOffset] = useState(0);
  const { data: cur } = useFetch("/costs");  // current Sydney month comes from the server
  const base = cur?.month;
  const { data, reload } = useFetch(base ? `/costs?month=${shift(base, offset)}` : "/costs");
  return (
    <div data-testid="costs-page">
      <PageHeader eyebrow="Monthly cost tracker" title="What MaSa costs this month." desc="Estimated from MaSa's own usage multiplied by the prices below, shown in Australian dollars. Check against your provider bills.">
        {base && [0, 1, 2].map((o) => (
          <Button key={o} size="sm" variant={o === offset ? "default" : "outline"} onClick={() => setOffset(o)} data-testid={`cost-month-${o}`}>{shift(base, o)}</Button>
        ))}
      </PageHeader>
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat testid="cost-total" label="Total so far" value={aud(data.total_aud)} sub={`${data.month} · budget ${aud(data.budget_aud)}`} tone={data.over_budget ? "text-[#FF3B30]" : ""} />
            <Stat testid="cost-forecast" label="Month forecast" value={aud(data.forecast_aud)} sub="at today's pace" />
            <Stat testid="cost-usage" label="Usage-based" value={aud(data.usage_aud)} />
            <Stat testid="cost-fixed" label="Fixed monthly" value={aud(data.fixed_aud)} />
          </div>
          <div className="grid lg:grid-cols-2 gap-3 items-start">
            <Panel title="By service" data-testid="cost-lines">
              <table className="w-full text-sm">
                <tbody>
                  {data.lines.map((l) => (
                    <tr key={l.key} className="border-b border-border/60" data-testid={`cost-line-${l.key}`}>
                      <td className="py-2">{l.label}{l.note && <div className="text-[11px] text-zinc-500">{l.note}</div>}</td>
                      <td className="py-2 text-right font-mono text-zinc-400">{l.units.toLocaleString()} {l.unit}</td>
                      <td className="py-2 text-right font-mono text-white">{aud(l.aud)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
            <Prices prices={data.prices} onSaved={reload} />
          </div>
          <ByCampaign data={data} />
        </div>
      )}
    </div>
  );
}
